import React from 'react'
import Modal from 'react-modal'
import classNames from 'classnames'
import {
	HiCheckCircle,
	HiOutlineInformationCircle,
	HiOutlineExclamation,
	HiOutlineExclamationCircle
} from 'react-icons/hi'
import { Button } from 'components/ui'
import { motion } from 'framer-motion'
import PropTypes from 'prop-types'

const ICON_WRAP =
	'!flex h-10 w-10 min-h-[40px] min-w-[40px] shrink-0 items-center justify-center overflow-hidden !rounded-full'

const StatusIcon = ({ status }) => {
	const wrap =
		"inline-flex h-10 w-10 min-h-10 min-w-10 shrink-0 items-center justify-center overflow-hidden rounded-full"

	const icons = {
		info: {
			wrap: `${wrap} bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-100`,
			icon: <HiOutlineInformationCircle className="h-5 w-5" />,
		},
		success: {
			wrap: `${wrap} bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-100`,
			icon: <HiCheckCircle className="h-5 w-5" />,
		},
		warning: {
			wrap: `${wrap} bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-100`,
			icon: <HiOutlineExclamationCircle className="h-5 w-5" />,
		},
		danger: {
			wrap: `${wrap} bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-100`,
			icon: <HiOutlineExclamation className="h-5 w-5" />,
		},
	}

	const current = icons[status]
	if (!current) return null

	return <span className={current.wrap}>{current.icon}</span>
}

const ConfirmDialog = props => {

	const {
		type,
		title,
		children,
		onCancel,
		onConfirm,
		cancelText,
		confirmText,
		loading,
		confirmButtonColor,
		isOpen,
		onClose,
		width = 520,
		closeTimeoutMS = 150,
		overlayClassName,
		portalClassName,
		shouldCloseOnOverlayClick = true,
		shouldCloseOnEsc = true,
		showCancel = true,
	} = props

	const handleClose = (e) => {
		onClose?.(e)
		onCancel?.()
	}

	const contentStyle = {
		content: {
			inset: 'unset',
			padding: 0,
			border: 'none',
			background: 'transparent',
			width: width,
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={handleClose}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
			shouldCloseOnEsc={shouldCloseOnEsc}
			ariaHideApp={false}
			closeTimeoutMS={closeTimeoutMS}
			className={{
				base: 'dialog flex items-center justify-center',
				afterOpen: 'dialog-after-open',
				beforeClose: 'dialog-before-close'
			}}
			overlayClassName={{
				base: classNames(
					'dialog-overlay fixed inset-0 bg-black/50 flex items-center justify-center',
					overlayClassName,
				),
				afterOpen: 'dialog-overlay-after-open',
				beforeClose: 'dialog-overlay-before-close'
			}}
			portalClassName={portalClassName}
			style={contentStyle}
		>
			<motion.div
				className="bg-white dark:bg-[#2d2d2d] rounded-lg shadow-lg w-full overflow-hidden"
				initial={{ scale: 0.9, opacity: 0 }}
				animate={{ scale: 1, opacity: 1 }}
				exit={{ scale: 0.9, opacity: 0 }}
				transition={{ duration: 0.2 }}
			>
				{/* Body */}
				<div className="px-6 py-6 flex items-start">
					<div className="shrink-0">
						<StatusIcon status={type} />
					</div>

					<div className="ml-4 min-w-0 flex-1 rtl:mr-4">
						<h4 className="mb-2">{title}</h4>
						{children}
					</div>
				</div>

				{/* Footer */}
				<div className="flex justify-end px-6 py-3 bg-gray-100 dark:bg-[#373737]">
					{showCancel ? (
						<Button
							type="button"
							size="sm"
							className="ltr:mr-2 rtl:ml-2 !bg-white !text-black hover:!bg-gray-200 dark:!bg-gray-300"
							onClick={handleClose}
						>
							{cancelText}
						</Button>
					) : null}

					<Button
						type="button"
						size="sm"
						variant="solid"
						onClick={() => onConfirm?.()}
						color={confirmButtonColor}
						loading={loading}
					>
						{confirmText}
					</Button>
				</div>
			</motion.div>
		</Modal>
	)
}

ConfirmDialog.propTypes = {
	type: PropTypes.oneOf(['info', 'success', 'warning', 'danger']),
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func,
}

ConfirmDialog.defaultProps = {
	type: 'info',
	cancelText: 'Cancel',
	confirmText: 'Confirm',
}


export default ConfirmDialog
